import { ComponentProps, ReactNode } from 'react';
import { ChecklistMenuField, ResolvedFieldProps, setValue } from '@frontend/design-system';

type ChecklistMenuFieldCustomRenderProps = Parameters<
  NonNullable<ComponentProps<typeof ChecklistMenuField>['customRender']>
>[0];

type SharedChecklistMenuFieldProps = Pick<
  ComponentProps<typeof ChecklistMenuField>,
  | 'customSearchComparator'
  | 'floatingStyleOverrides'
  | 'label'
  | 'name'
  | 'placement'
  | 'placeholder'
  | 'searchContainerStyles'
  | 'showSearchBar'
  | 'showSelectAll'
  | 'showSelectionBar'
  | 'sortLabelsBySelection'
  | 'topContent'
> & {
  trackingId?: string;
};

type ChecklistChipMenuFieldProps = SharedChecklistMenuFieldProps & {
  children: ReactNode;
  chipVariant?: ComponentProps<typeof ChecklistMenuField.ChipInput>['variant'];
  fieldProps: ResolvedFieldProps<'checklist'>;
  onChange: (value: string[]) => void;
};

const getNextChecklistValue = (fieldProps: ResolvedFieldProps<'checklist'>, nextValue: string | string[]) => {
  return setValue({ ...fieldProps, type: 'checklist' }, nextValue).value;
};

const ChecklistChipInput = ({
  chipVariant,
  onRemoveClick,
  ...props
}: ChecklistMenuFieldCustomRenderProps & {
  chipVariant?: ComponentProps<typeof ChecklistMenuField.ChipInput>['variant'];
  onRemoveClick: (id: string) => void;
}) => {
  return <ChecklistMenuField.ChipInput {...props} variant={chipVariant} onRemoveClick={onRemoveClick} />;
};

export const ChecklistChipMenuField = ({
  children,
  chipVariant = 'primary',
  fieldProps,
  floatingStyleOverrides,
  name,
  onChange,
  trackingId,
  ...props
}: ChecklistChipMenuFieldProps) => {
  const handleRemoveClick = (id: string) => {
    fieldProps.onChange({ name, value: [id] });
    onChange(getNextChecklistValue(fieldProps, id));
  };

  const handleChange = (event?: { name: string; value: string | string[] }) => {
    if (!event) {
      return;
    }

    fieldProps.onChange(event);
    onChange(getNextChecklistValue(fieldProps, event.value));
  };

  const checklistMenuFieldProps = {
    ...fieldProps,
    ...props,
    customRender: (renderProps: ChecklistMenuFieldCustomRenderProps) => (
      <ChecklistChipInput {...renderProps} chipVariant={chipVariant} onRemoveClick={handleRemoveClick} />
    ),
    name,
    onChange: handleChange as NonNullable<ComponentProps<typeof ChecklistMenuField>['onChange']>,
    ...(trackingId ? { trackingId } : {}),
  } as ComponentProps<typeof ChecklistMenuField>;

  return (
    <ChecklistMenuField
      {...checklistMenuFieldProps}
      floatingStyleOverrides={[{ maxWidth: 'initial' }, floatingStyleOverrides]}
    >
      {children}
    </ChecklistMenuField>
  );
};
